import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

// Define TypeScript interfaces for our data
interface Course {
  id: number,
  title: string;
  subject: "History" | "Political Science" | "Geography" | "Economics" | "Hindi Literature" | "Home Science" | "Sociology";
  instructor: string;
  rating: number;
  reviewCount: number;
  price: number;
  discountPrice: number;
  imageUrl: string;
  board: string;
  mode: string;
  badges?: string[];
  topics: string[];
  description: string;
}

interface ArtsCourseCardProps {
  course: Course;
}

const ArtsCourseCard: React.FC<ArtsCourseCardProps> = ({ course }) => {
  const [isHovered, setIsHovered] = useState(false);
  
  // Calculate discount percentage
  const discountPercentage = Math.round(
    ((course.price - course.discountPrice) / course.price) * 100
  );
  
  // Subject-specific gradients
  const subjectGradients: Record<Course['subject'], string> = {
    "History": "from-amber-500 to-orange-600",
    "Political Science": "from-red-500 to-rose-600",
    "Geography": "from-teal-500 to-green-600",
    "Economics": "from-blue-500 to-cyan-600",
    "Hindi Literature": "from-yellow-500 to-amber-600",
    "Home Science": "from-purple-500 to-indigo-600",
    "Sociology": "from-pink-500 to-rose-600"
  };
  
  const gradient = subjectGradients[course.subject] || "from-blue-500 to-indigo-600";
  
  // Render star rating
  const renderStars = (rating: number) => {
    return (
      <div className="flex">
        {[1, 2, 3, 4, 5].map((star) => (
          <span key={star} className={star <= Math.round(rating) ? "text-yellow-400" : "text-gray-300"}>
            ★
          </span>
        ))}
      </div>
    );
  };
  
  return (
    <div 
      className="w-72 bg-white rounded-xl shadow-lg overflow-hidden transform transition-all duration-300 hover:shadow-xl"
      style={{ transform: isHovered ? 'translateY(-8px)' : 'none' }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Card Header with Image */}
      <div className={`relative h-40 bg-gradient-to-r ${gradient}`}>
        <img 
          src={course.imageUrl} 
          alt={course.title}
          className="w-full h-full object-cover  opacity-100 rounded-xl"  //mix-blend-overlay
        />
        
        {/* Badges */}
        <div className="absolute top-3 left-3 flex gap-2">
          {course.badges && course.badges.map((badge: string, index: number) => (
            <span 
              key={index} 
              className="px-2 py-1 bg-white/90 text-xs font-bold rounded-full shadow-sm text-purple-700"
            >
              {badge}
            </span>
          ))}
        </div>
        
        {/* Medium indicator */}
        <div className="absolute top-3 right-3 z-10">
          <div className="bg-white rounded-md shadow-md">
            <span className="px-2 py-1 text-xs font-medium text-blue-700">
              हिंदी माध्यम
            </span>
          </div>
        </div>
      </div>
      
      {/* Card Content */}
      <div className="p-5">
        <h3 className="text-lg font-bold text-gray-800 line-clamp-2 h-14">{course.title}</h3>
        
        <p className="text-sm text-gray-600 mt-2">by {course.instructor}</p>
        
        <div className="flex items-center mt-2">
          <span className="text-amber-500 font-bold mr-1">{course.rating}</span>
          {renderStars(course.rating)}
          <span className="text-xs text-gray-500 ml-1">({course.reviewCount})</span>
        </div>
        
        <div className="flex items-center mt-3 text-sm text-gray-600">
          <span className="flex items-center mr-4">
            <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            {course.mode}
          </span>
          <span className="flex items-center">
            <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 10V3L4 14h7v7l9-11h-7z" />
            </svg>
            {course.board}
          </span>
        </div>
        
        {/* Topics tags */}
        <div className="flex flex-wrap gap-1 mt-3">
          {course.topics.map((topic: string, index: number) => (
            <span 
              key={index} 
              className="px-2 py-1 bg-blue-100 text-blue-700 text-xs rounded-full"
            >
              {topic}
            </span>
          ))}
        </div>
        
        {/* Price and CTA */}
        <div className="mt-5 pt-4 border-t border-gray-100 flex justify-between items-center">
          <div>
            <span className="text-lg font-bold text-gray-800">₹{course.discountPrice}</span>
            <span className="text-sm text-gray-500 line-through ml-2">₹{course.price}</span>
            <span className="ml-2 text-green-600 text-xs font-bold">{discountPercentage}% छूट</span>
          </div>
          
          <Link to={`/coursepage/${course.id}`}
            className={`px-3 py-2 rounded-lg font-medium text-sm text-white bg-gradient-to-r ${gradient} transition-all hover:shadow-md cursor-pointer`}
          >
            अभी जुड़ें
          </Link>
        </div>
      </div>
      
      {/* Quick Preview Overlay (appears on hover) */}
      <div 
        className={`absolute inset-0 bg-gradient-to-t from-black/80 to-transparent flex flex-col justify-end p-5 transition-opacity duration-300 ${
          isHovered ? 'opacity-100' : 'opacity-0 pointer-events-none'
        }`}
      >
        <h3 className="text-lg font-bold text-white">{course.title}</h3>
        <p className="text-white/80 text-sm my-2">
          {course.description}
        </p>
        
        <div className="flex gap-2 mt-3">
          <Link to={`/coursepage/${course.id}`} className="flex-1 bg-white text-purple-700 py-2 rounded-lg font-medium text-sm hover:bg-gray-100 transition-colors cursor-pointer">
            विवरण देखें
          </Link>
          <Link to={`/coursepage/${course.id}`} className="flex-1 bg-gradient-to-r from-purple-500 to-indigo-600 text-white py-2 rounded-lg font-medium text-sm hover:from-purple-600 hover:to-indigo-700 transition-colors cursor-pointer">
            अभी जुड़ें
          </Link>
        </div>
      </div>
    </div>
  );
};

const ArtsCoursesApp = () => {
  const [activeClass, setActiveClass] = useState('class12');

  // Add these state variables for scroll indicators
  const [showLeftArrow, setShowLeftArrow] = useState(false);
  const [showRightArrow, setShowRightArrow] = useState(true);
  
  // Add this useEffect to handle scroll indicators
  useEffect(() => {
    const container = document.getElementById('courseScrollContainerArts');
    
    if (container) {
      const handleScroll = () => {
        const isNotAtStart = container.scrollLeft > 10;
        const isNotAtEnd = container.scrollLeft < (container.scrollWidth - container.clientWidth - 10);
        
        setShowLeftArrow(isNotAtStart);
        setShowRightArrow(isNotAtEnd);
      };
      
      // Initial check
      handleScroll();
      
      // Add scroll event listener
      container.addEventListener('scroll', handleScroll);
      
      // Cleanup
      return () => container.removeEventListener('scroll', handleScroll);
    }
  }, [activeClass]); // Re-run when active class changes
  
  // Add these scroll handler functions
  const scrollLeft = () => {
    const container = document.getElementById('courseScrollContainer');
    if (container) {
      container.scrollBy({ left: -300, behavior: 'smooth' });
    }
  };
  
  const scrollRight = () => {
    const container = document.getElementById('courseScrollContainer');
    if (container) {
      container.scrollBy({ left: 300, behavior: 'smooth' });
    }
  };
  
  // Class 12 Courses Data
  const class12Courses: Course[] = [
    {
      id : 8,
      title: "कक्षा 12 इतिहास - संपूर्ण पाठ्यक्रम",
      subject: "History",
      instructor: "Danish सर",
      rating: 4.9,
      reviewCount: 1256,
      price: 1999,
      discountPrice: 199,
      imageUrl: "https://naendapi.naecoaching.com/media/courses/14_20241025_163432_0013.png",
      board: "बिहार बोर्ड",
      mode: "ऑनलाइन + ऑफलाइन",
      badges: ["बेस्टसेलर", "उच्च अंक"],
      topics: ["प्राचीन इतिहास", "मध्यकालीन इतिहास", "आधुनिक इतिहास"],
      description: "बिहार बोर्ड के लिए तैयार किया गया इतिहास का यह कोर्स आपको परीक्षा में अधिकतम अंक प्राप्त करने के लिए तैयार करेगा। पीडीएफ नोट्स, क्लास नोट्स और अभ्यास परीक्षण शामिल हैं।"
    },
    {
      id : 9,
      title: "कक्षा 12 राजनीति विज्ञान - संपूर्ण पाठ्यक्रम",
      subject: "Political Science",
      instructor: "Danish सर",
      rating: 4.8,
      reviewCount: 1078,
      price: 1999,
      discountPrice: 199,
      imageUrl: "https://naendapi.naecoaching.com/media/courses/15_20241025_163432_0014.png",
      board: "बिहार बोर्ड",
      mode: "ऑनलाइन + ऑफलाइन",
      badges: ["लोकप्रिय", "उच्च सफलता दर"],
      topics: ["भारतीय राजनीति", "अंतर्राष्ट्रीय संबंध", "राजनीतिक सिद्धांत"],
      description: "राजनीति विज्ञान के सभी महत्वपूर्ण विषयों को कवर करने वाला व्यापक कोर्स जो बिहार बोर्ड परीक्षा के लिए आवश्यक है। वर्तमान घटनाओं के अपडेट के साथ अध्ययन सामग्री शामिल है।"
    },
    {
      id : 7,
      title: "कक्षा 12 भूगोल - संपूर्ण पाठ्यक्रम",
      subject: "Geography",
      instructor: "Danish सर",
      rating: 4.9,
      reviewCount: 982,
      price: 1999,
      discountPrice: 199,
      imageUrl: "https://naendapi.naecoaching.com/media/courses/11_20241025_163431_0010.png",
      board: "बिहार बोर्ड",
      mode: "ऑनलाइन + ऑफलाइन",
      badges: ["उच्च अंक", "नक्शा आधारित"],
      topics: ["भौतिक भूगोल", "मानव भूगोल", "आर्थिक भूगोल"],
      description: "विस्तृत मानचित्र अध्ययन, केस स्टडी और सरल उदाहरणों के साथ भूगोल कोर्स। पीडीएफ नोट्स और नियमित परीक्षण शामिल हैं।"
    },
    {
      id : 36,
      title: "कक्षा 12 अर्थशास्त्र - संपूर्ण पाठ्यक्रम",
      subject: "Economics",
      instructor: "MG Hussain सर",
      rating: 4.7,
      reviewCount: 1345,
      price: 1999,
      discountPrice: 199,
      imageUrl: "https://naendapi.naecoaching.com/media/courses/Economics_12.png",
      board: "बिहार बोर्ड",
      mode: "ऑनलाइन + ऑफलाइन",
      badges: ["अवधारणात्मक", "ग्राफ आधारित"],
      topics: ["व्यष्टि अर्थशास्त्र", "समष्टि अर्थशास्त्र", "भारतीय अर्थव्यवस्था"],
      description: "अर्थशास्त्र के जटिल विषयों को सरल बनाने वाला कोर्स जिसमें ग्राफ़, चार्ट और उदाहरणों के माध्यम से सिद्धांत समझाए गए हैं।"
    },
    {
      id : 15,
      title: "कक्षा 12 हिंदी साहित्य - संपूर्ण पाठ्यक्रम",
      subject: "Hindi Literature",
      instructor: "MG Hussain सर",
      rating: 4.9,
      reviewCount: 1122,
      price: 1999,
      discountPrice: 199,
      imageUrl: "https://naendapi.naecoaching.com/media/courses/Blue_White_Business_Entrepreneurial_Tutorial_Youtube_Channel_Art_20241029_210440_0000.png",
      board: "बिहार बोर्ड",
      mode: "ऑनलाइन + ऑफलाइन",
      badges: ["उच्च अंक", "साहित्यिक विश्लेषण"],
      topics: ["गद्य", "पद्य", "व्याकरण"],
      description: "हिंदी साहित्य का गहन अध्ययन जिसमें कविताओं और गद्य की व्याख्या, व्याकरण और निबंध लेखन शामिल है। बोर्ड परीक्षा में उच्च अंक प्राप्त करने के लिए आवश्यक सभी सामग्री।"
    },
    {
      id : 1,
      title: "कक्षा 12 गृह विज्ञान - संपूर्ण पाठ्यक्रम",
      subject: "Home Science",
      instructor: "Danish सर",
      rating: 4.8,
      reviewCount: 956,
      price: 1999,
      discountPrice: 199,
      imageUrl: "https://naendapi.naecoaching.com/media/courses/18_20241025_163432_0017.png",
      board: "बिहार बोर्ड",
      mode: "ऑनलाइन + ऑफलाइन",
      badges: ["केस स्टडी", "अवधारणात्मक"],
      topics: ["विकासात्मक मनोविज्ञान", "सामाजिक मनोविज्ञान", "असामान्य मनोविज्ञान"],
      description: "मनोविज्ञान के सिद्धांतों और अवधारणाओं को सरल तरीके से समझाने वाला कोर्स। केस स्टडीज और वास्तविक जीवन के उदाहरणों से समृद्ध।"
    },
    // {
    //   id : 1,
    //   title: "कक्षा 12 समाजशास्त्र - संपूर्ण पाठ्यक्रम",
    //   subject: "Sociology",
    //   instructor: "Danish सर",
    //   rating: 4.7,
    //   reviewCount: 890,
    //   price: 1999,
    //   discountPrice: 199,
    //   imageUrl: "/api/placeholder/400/240",
    //   board: "बिहार बोर्ड",
    //   mode: "ऑनलाइन + ऑफलाइन",
    //   badges: ["अवधारणात्मक", "समकालीन मुद्दे"],
    //   topics: ["समाज की अवधारणा", "सामाजिक संस्थाएँ", "सामाजिक परिवर्तन"],
    //   description: "समाजशास्त्र के मूल सिद्धांतों और बिहार बोर्ड पाठ्यक्रम के अनुसार महत्वपूर्ण विषयों का अध्ययन। समकालीन सामाजिक मुद्दों पर विशेष फोकस।"
    // }
  ];
  
  // Class 11 Courses Data
  const class11Courses: Course[] = [
    {
      id : 12,
      title: "कक्षा 11 इतिहास - संपूर्ण पाठ्यक्रम",
      subject: "History",
      instructor: "Danish सर",
      rating: 4.9,
      reviewCount: 1145,
      price: 6999,
      discountPrice: 3499,
      imageUrl: "https://naendapi.naecoaching.com/media/courses/13_20241025_163432_0012.png",
      board: "बिहार बोर्ड",
      mode: "ऑनलाइन + ऑफलाइन",
      badges: ["फाउंडेशन कोर्स", "उच्च रेटिंग"],
      topics: ["विश्व इतिहास", "भारतीय इतिहास", "सभ्यताओं का विकास"],
      description: "इतिहास की मजबूत नींव रखने वाला कोर्स जो कक्षा 12 और आगे के अध्ययन के लिए तैयार करता है। विस्तृत नोट्स, मानचित्र और टाइमलाइन के साथ।"
    },
    {
      id : 13,
      title: "कक्षा 11 राजनीति विज्ञान - संपूर्ण पाठ्यक्रम",
      subject: "Political Science",
      instructor: "Danish सर",
      rating: 4.8,
      reviewCount: 967,
      price: 6999,
      discountPrice: 3499,
      imageUrl: "https://naendapi.naecoaching.com/media/courses/16_20241025_163432_0015.png",
      board: "बिहार बोर्ड",
      mode: "ऑनलाइन + ऑफलाइन",
      badges: ["फाउंडेशन", "अवधारणात्मक"],
      topics: ["राजनीतिक सिद्धांत", "भारतीय संविधान", "सरकार के प्रकार"],
      description: "राजनीति विज्ञान के मूल सिद्धांतों और अवधारणाओं की समझ विकसित करने वाला कोर्स। भारतीय राजनीतिक व्यवस्था पर विशेष फोकस।"
    },
    {
      id : 11,
      title: "कक्षा 11 भूगोल - संपूर्ण पाठ्यक्रम",
      subject: "Geography",
      instructor: "Danish सर",
      rating: 4.8,
      reviewCount: 890,
      price: 6999,
      discountPrice: 3499,
      imageUrl: "https://naendapi.naecoaching.com/media/courses/12_20241025_163431_0011.png",
      board: "बिहार बोर्ड",
      mode: "ऑनलाइन + ऑफलाइन",
      badges: ["नक्शा आधारित", "अवधारणात्मक"],
      topics: ["भौतिक भूगोल", "जलवायु", "भू-आकृति विज्ञान"],
      description: "भूगोल की मूलभूत अवधारणाओं और सिद्धांतों को समझाने वाला कोर्स। रंगीन मानचित्र, डायग्राम और वीडियो के माध्यम से अध्ययन सामग्री।"
    },
    {
      id : 35,
      title: "कक्षा 11 अर्थशास्त्र - संपूर्ण पाठ्यक्रम",
      subject: "Economics",
      instructor: "MG Hussain सर",
      rating: 4.7,
      reviewCount: 1056,
      price: 6999,
      discountPrice: 3499,
      imageUrl: "https://naendapi.naecoaching.com/media/courses/Economics_11.png",
      board: "बिहार बोर्ड",
      mode: "ऑनलाइन + ऑफलाइन",
      badges: ["फाउंडेशन", "अवधारणात्मक"],
      topics: ["अर्थशास्त्र का परिचय", "आर्थिक मॉडल", "बाजार और अर्थव्यवस्था"],
      description: "अर्थशास्त्र की मूलभूत अवधारणाओं को सरल भाषा में समझाने वाला कोर्स। व्यावहारिक उदाहरणों और आरेखों के माध्यम से सिद्धांतों का स्पष्टीकरण।"
    },
    {
      id : 15,
      title: "कक्षा 11 हिंदी साहित्य - संपूर्ण पाठ्यक्रम",
      subject: "Hindi Literature",
      instructor: "Danish सर",
      rating: 4.9,
      reviewCount: 987,
      price: 6999,
      discountPrice: 3499,
      imageUrl: "https://naendapi.naecoaching.com/media/courses/Blue_White_Business_Entrepreneurial_Tutorial_Youtube_Channel_Art_20241029_210440_0000.png",
      board: "बिहार बोर्ड",
      mode: "ऑनलाइन + ऑफलाइन",
      badges: ["साहित्यिक फाउंडेशन", "व्याकरण"],
      topics: ["काव्य", "गद्य", "रचना"],
      description: "हिंदी साहित्य के इतिहास, प्रमुख रचनाकारों और साहित्यिक धाराओं का अध्ययन। व्याकरण और रचना कौशल पर विशेष ध्यान।"
    },
    {
      id : 14,
      title: "कक्षा 11 गृह विज्ञान - संपूर्ण पाठ्यक्रम",
      subject: "Home Science",
      instructor: "Danish सर",
      rating: 4.8,
      reviewCount: 845,
      price: 6999,
      discountPrice: 3499,
      imageUrl: "/api/placeholder/400/240",
      board: "बिहार बोर्ड",
      mode: "ऑनलाइन + ऑफलाइन",
      badges: ["अवधारणात्मक", "प्रयोगात्मक"],
      topics: ["मनोविज्ञान का परिचय", "मस्तिष्क और व्यवहार", "संज्ञानात्मक प्रक्रियाएँ"],
      description: "मनोविज्ञान के मूलभूत सिद्धांतों और अवधारणाओं का परिचय। रोचक प्रयोगों और केस स्टडीज के माध्यम से समझ विकसित करना।"
    },
    // {
    //   id : 1,
    //   title: "कक्षा 11 समाजशास्त्र - संपूर्ण पाठ्यक्रम",
    //   subject: "Sociology",
    //   instructor: "Danish सर",
    //   rating: 4.7,
    //   reviewCount: 789,
    //   price: 6999,
    //   discountPrice: 3499,
    //   imageUrl: "/api/placeholder/400/240",
    //   board: "बिहार बोर्ड",
    //   mode: "ऑनलाइन + ऑफलाइन",
    //   badges: ["अवधारणात्मक", "फाउंडेशन"],
    //   topics: ["समाजशास्त्र का परिचय", "सामाजिक संरचना", "सामाजीकरण"],
    //   description: "समाजशास्त्र के मूलभूत सिद्धांतों और अवधारणाओं का परिचय। समाज के विभिन्न पहलुओं और सामाजिक प्रक्रियाओं की समझ विकसित करना।"
    // }
  ];

  return (
    <div className=" bg-gray-100 p-6">
      {/* Header and Toggle Buttons */}
      <div className="max-w-6xl mx-auto mb-8">
        <h1 className="text-3xl font-bold text-center text-gray-800 mb-6">कला संकाय पाठ्यक्रम</h1>
        
        <div className="flex justify-center gap-4 mb-6">
          <button
            onClick={() => setActiveClass('class11')}
            className={`px-6 py-3 rounded-lg font-medium text-sm transition-all ${
              activeClass === 'class11'
                ? 'bg-gradient-to-r from-purple-500 to-indigo-600 text-white shadow-md'
                : 'bg-white text-gray-700 hover:bg-gray-100'
            }`}
          >
            कक्षा 11 कला
          </button>
          
          <button
            onClick={() => setActiveClass('class12')}
            className={`px-6 py-3 rounded-lg font-medium text-sm transition-all ${
              activeClass === 'class12'
                ? 'bg-gradient-to-r from-purple-500 to-indigo-600 text-white shadow-md'
                : 'bg-white text-gray-700 hover:bg-gray-100'
            }`}
          >
            कक्षा 12 कला
          </button>
        </div>
        
        {/* Active class description */}
        <div className="text-center text-gray-600 mb-6">
          {activeClass === 'class11' ? (
            <p>कक्षा 11 के छात्रों के लिए फाउंडेशन कोर्स। मजबूत मूलभूत ज्ञान विकसित करने पर फोकस।</p>
          ) : (
            <p>कक्षा 12 बोर्ड परीक्षाओं और प्रतियोगी परीक्षाओं के लिए पूर्ण तैयारी।</p>
          )}
        </div>
      </div>
      
      {/* Course Cards */}
      <div className="relative max-w-6xl mx-auto">
        {/* Left scroll indicator */}
        <div 
          className={`absolute left-2 top-1/2 transform -translate-y-1/2 z-10 bg-white/90 p-3 rounded-full shadow-lg cursor-pointer transition-all duration-300 hover:bg-purple-100 ${showLeftArrow ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}
          onClick={scrollLeft}
        >
          <svg className="w-5 h-5 text-purple-700" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M15 19l-7-7 7-7" />
          </svg>
        </div>

        {/* Right scroll indicator */}
        <div 
          className={`absolute right-2 top-1/2 transform -translate-y-1/2 z-10 bg-white/90 p-3 rounded-full shadow-lg cursor-pointer transition-all duration-300 hover:bg-purple-100 ${showRightArrow ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}
          onClick={scrollRight}
        >
          <svg className="w-5 h-5 text-purple-700" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M9 5l7 7-7 7" />
          </svg>
        </div>
        
        {/* Scroll container */}
        <div className="flex overflow-x-auto gap-6 pb-4 scroll-container hide-scrollbar" id="courseScrollContainerArts">
          <div className="flex flex-nowrap gap-6">
            {(activeClass === 'class11' ? class11Courses : class12Courses).map((course, index) => (
              <ArtsCourseCard key={index} course={course} />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ArtsCoursesApp;